import {
  makeVar,
  skipToken,
  useReactiveVar,
  useSuspenseQuery
} from '@apollo/client'

import { createContext, useCallback, useEffect } from 'react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { FieldErrors } from 'react-hook-form'

import { CustomerFormData, FormData, RoomsFormData } from './useBillingForm'
import { useCurrentUser } from '@/features/user'

export const BillingContext = createContext<{
  billing: Partial<GraphQL.BillingFragmentFragment> | null
  isNew: boolean
  serverErrors?: FieldErrors<FormData>
  save: (data: FormData) => Promise<void | undefined>
  update: (newBilling: RoomsFormData | CustomerFormData) => void
}>({
  billing: null,
  isNew: true,
  update: () => {},
  save: () => Promise.reject()
})

const billingVar = makeVar<Partial<
  GraphQL.BillingFragmentFragment & { acceptedContract: boolean }
> | null>(null)

export function useBillingData() {
  const { user } = useCurrentUser()

  const { data } = useSuspenseQuery(AdminAPI.billing.GET, {
    errorPolicy: 'ignore'
  })

  const { data: countData } = useSuspenseQuery(
    AdminAPI.space.COUNT,
    data ? skipToken : { fetchPolicy: 'network-only' }
  )

  useEffect(() => {
    if (billingVar() != null) return

    if (!data) {
      billingVar({
        rooms: countData?.countSpaces || 150,
        interval: GraphQL.BillingInterval.Yearly,
        email: user.email,
        contactName: `${user.givenName} ${user.lastName}`,
        acceptedContract: false
      })

      return
    }

    const billing = getFragment(
      SharedAPI.billing.BILLING_FRAGMENT,
      data.getBilling
    )

    billingVar(billing)
  }, [data, countData, user])

  const billing = useReactiveVar(billingVar)

  const setBilling = useCallback(
    (newBilling: RoomsFormData | CustomerFormData) => {
      const currentBilling = billingVar()

      billingVar({
        ...currentBilling,
        ...newBilling
      })
    },
    []
  )

  return {
    billing,
    setBilling,
    isNew: !data
  }
}
